import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Button from '@components/button/Button';
import { Spinner } from '@components/index';
import InfiniteScroll from '@components/infinite-scroll/InfiniteScroll';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import useNotify from '@hooks/notify';
import { getLocalStorageItem } from '@utils/storage';

import DeleteConfirmationModalV2 from './DeleteConfirmationModalV2';
import EmptyTabState from './EmptyTabState';
import SaveVoiceNoteModal, { VoiceRecording } from './SaveVoiceNoteModal';
import VoiceNoteItem from './VoiceNoteItem';
import VoiceRecordingBar from './VoiceRecordingBar';

import { LEAD_ACTIVITY_LIMIT } from '../../constants/leadActivity';
import useCanEditLead from '../../hooks/useCanEditLead';
import useVoiceRecorder, {
  isVoiceRecordingSupported
} from '../../hooks/useVoiceRecorder';
import {
  useDeleteLeadVoiceNoteMutation,
  useLazyGetFileUrlQuery,
  useLazyGetLeadVoiceNotesQuery
} from '../../queries/LeadManagementQuery';
import { LeadVoiceNote } from '../../types/leadVoiceNote';
import { VOICE_NOTE_MAX_DURATION_SEC } from '../../utils/voiceNote';

const VoiceNotesTab: React.FC<{ leadId: string }> = ({ leadId }) => {
  const organizationId =
    getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID) || '';
  const { currentOrg } = useSelector((state: any) => state.user);
  const canEditLead = useCanEditLead();
  const { createSuccessAlert, createErrorAlert, createWarningAlert } =
    useNotify();

  const [getVoiceNotes] = useLazyGetLeadVoiceNotesQuery();
  const [getFileUrl] = useLazyGetFileUrlQuery();
  const [deleteVoiceNote, { isLoading: isDeleting }] =
    useDeleteLeadVoiceNoteMutation();

  const [voiceNotes, setVoiceNotes] = useState<LeadVoiceNote[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [recording, setRecording] = useState<VoiceRecording | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [canRecord] = useState(isVoiceRecordingSupported);
  const recorder = useVoiceRecorder({
    maxDurationSec: VOICE_NOTE_MAX_DURATION_SEC,
    onComplete: (file, durationSec, levels) =>
      setRecording({ file, durationSec, levels }),
    onError: createErrorAlert,
    onLimitReached: () =>
      createWarningAlert('Recording stopped at the 10 minute limit.')
  });
  const isRecording = recorder.status !== 'idle';
  // InfiniteScroll keeps its first handleScroll, so paging state lives here.
  const pagingRef = useRef({ page: 0, hasMore: false, isFetching: false });

  const load = useCallback(
    async (page: number) => {
      pagingRef.current.isFetching = true;
      if (page === 0) setIsLoading(true);
      else setIsLoadingMore(true);
      try {
        const res = await getVoiceNotes({
          organizationId,
          leadId,
          page,
          limit: LEAD_ACTIVITY_LIMIT
        }).unwrap();
        const data = res.data ?? [];
        const more =
          res.pagination?.last_page === false &&
          data.length === LEAD_ACTIVITY_LIMIT;
        setVoiceNotes((prev) => (page === 0 ? data : [...prev, ...data]));
        setTotal(res.pagination?.total_rows ?? data.length);
        setHasMore(more);
        pagingRef.current = { page, hasMore: more, isFetching: false };
        setHasError(false);
      } catch {
        pagingRef.current.isFetching = false;
        setHasError(true);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [getVoiceNotes, leadId, organizationId]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  const loadMore = useCallback(() => {
    const { page, hasMore: canLoad, isFetching } = pagingRef.current;
    if (canLoad && !isFetching) load(page + 1);
  }, [load]);

  const loadVoiceNoteUrl = useCallback(
    async (documentId: string) => {
      const response = await getFileUrl({ organizationId, documentId });
      return response.data?.url;
    },
    [getFileUrl, organizationId]
  );

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteVoiceNote({
        organizationId,
        leadId,
        voiceNoteId: deleteId
      }).unwrap();
      sendMixpanelEvent(Event.LEAD_VOICE_NOTE_DELETED, {
        org_id: organizationId,
        org_name: currentOrg?.name,
        lead_id: leadId,
        voice_note_id: deleteId
      });
      createSuccessAlert('Voice note deleted successfully');
      load(0);
    } catch {
      createErrorAlert('Failed to delete voice note. Please try again.');
    } finally {
      setDeleteId(null);
    }
  };

  const renderContent = () => {
    if (isLoading && voiceNotes.length === 0) {
      return <Spinner className="size-6" />;
    }
    // Older backends have no voice notes API.
    if (hasError) {
      return (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <p className="text-base text-grey80">
            Voice notes are not available right now. Please try again later.
          </p>
          <Button
            variant="tertiary"
            label="Retry"
            className="px-4 py-2"
            handleClick={() => load(0)}
          />
        </div>
      );
    }
    if (voiceNotes.length === 0) {
      return (
        <EmptyTabState
          title="No voice notes added yet!"
          subtitle="Voice notes recorded for this lead will appear here"
        />
      );
    }
    return (
      <div className="custom-scroll flex max-h-125 flex-col gap-6 overflow-y-auto">
        {voiceNotes.map((voiceNote) => (
          <VoiceNoteItem
            key={voiceNote.id}
            voiceNote={voiceNote}
            onDeleteClick={setDeleteId}
            loadSrc={loadVoiceNoteUrl}
          />
        ))}
        <InfiniteScroll
          handleScroll={loadMore}
          isDisable={!hasMore}
          isLoading={isLoadingMore}
          spinnerStyle="w-6 h-6"
        />
      </div>
    );
  };

  return (
    <div className="relative flex min-h-70 flex-col gap-6 px-6 py-3">
      <div className="flex items-center justify-between">
        <span className="text-18 font-semibold text-cofeeNeutral10">
          {total} {total === 1 ? 'voice note' : 'voice notes'}
        </span>
        {canEditLead && canRecord && !hasError && (
          <Button
            label="Record voice"
            leftIconName="mic-white"
            handleClick={recorder.start}
            disabled={isRecording || !!recording}
          />
        )}
      </div>
      {renderContent()}

      {isRecording && (
        <div className="sticky bottom-4 z-10 flex justify-center">
          <VoiceRecordingBar recorder={recorder} />
        </div>
      )}
      {recording && (
        <SaveVoiceNoteModal
          organizationId={organizationId}
          leadId={leadId}
          recording={recording}
          onClose={() => setRecording(null)}
          onSaved={() => load(0)}
        />
      )}
      {deleteId && (
        <DeleteConfirmationModalV2
          showDeleteModal
          title="Delete Voice Note"
          message="Are you sure you want to delete this voice note? This action cannot be undone."
          handleConfirmDelete={handleConfirmDelete}
          handleCancel={() => setDeleteId(null)}
          isConfirmButtonLoading={isDeleting}
        />
      )}
    </div>
  );
};

export default VoiceNotesTab;
